// @flow

import _ from 'lodash';
import memoize from 'memoize-weak';
import React, { type Node } from 'react';
import bemify, { toModifiers } from 'bemify-js';

import Icon from 'app/common/components/Icon';
import LogoImage from 'app/common/components/LogoImage';
import Tag from 'app/common/components/Tag';
import shouldComponentUpdate from 'app/perf-utils/components/shouldComponentUpdate';
import { emptyObject } from 'app/common/CommonUtils';
import { sortNodes } from 'app/beta-editor/utils/NodeTrees';
import 'app/common/components/ZapStepIcons.scss';

import type { FlowNode, NodeMap } from 'app/beta-editor/flowState';
import type { KindType } from 'app/common/types';

const bem = bemify('zap-icons');

export type ZapStepIconType = {
  key: string,
  isPremium?: boolean,
  isHidden?: boolean,
};

type ZapStepIconProps = {
  step: ZapStepIconType,
  customConnector?: Node,
  connectIconType: 'zap-arrow' | 'plus' | 'arrow-right',
  isLastStep: boolean,
};

export class ZapStepIcon extends React.Component<ZapStepIconProps> {
  static defaultProps = {
    connectIconType: 'zap-arrow',
  };

  shouldComponentUpdate = shouldComponentUpdate.deepCheckProps;

  render() {
    const { step } = this.props;
    return (
      <li className={bem('__step')}>
        <div
          className={bem({
            '__icon-wrap': !step.isHidden,
            '__hidden-wrap': step.isHidden,
          })}
        >
          {step.isHidden ? (
            step.key
          ) : (
            <LogoImage
              className={bem('__icon')}
              logoClass={step.key}
              logoSize={64}
            />
          )}
          {step.isPremium ? (
            <Tag className={bem('__tag')} kind="premium">
              Premium
            </Tag>
          ) : null}
        </div>
        {this.props.customConnector && !this.props.isLastStep ? (
          this.props.customConnector
        ) : (
          <Icon className={bem('__caret')} type={this.props.connectIconType} />
        )}
      </li>
    );
  }
}

export const truncateStepsToMax = (max: number, steps: ZapStepIconType[]) => {
  const first = _.head(steps);
  const last = _.last(steps);
  // If `max < 3` we can't render a middle step at all
  const middle =
    max < 3
      ? []
      : steps.length <= max
      ? steps.slice(1, steps.length - 1) // all middle steps
      : [{ key: steps.length - 2 + '', isPremium: false, isHidden: true }]; // count of middle steps
  return _.compact([first, ...middle, last]).slice(0, max);
};

type ZapStepIconsProps = {
  max: number,
  className?: string,
  kind?: KindType,
  steps: ZapStepIconType[],
  // TODO: Remove customConnector after CompactZapTemplatePreview experiment.
  // Introduced customConnector so that we can use a different variation of zap-arrow.
  customConnector?: Node,
  connectIconType?: 'zap-arrow' | 'plus' | 'arrow-right',
};

type Zap = {
  nodes?: FlowNode[] | NodeMap,
};

const isLastStep = (steps, currentIndex) => steps.length - 1 === currentIndex;

class ZapStepIcons extends React.Component<ZapStepIconsProps> {
  static stepsFromZap: (zap: Zap) => ZapStepIconType[];

  shouldComponentUpdate = shouldComponentUpdate.deepCheckProps;

  truncatedSteps() {
    const { max, steps } = this.props;
    return truncateStepsToMax(max, steps);
  }

  render() {
    const steps = this.truncatedSteps();
    const icons = steps.map((step, i) => (
      <ZapStepIcon
        connectIconType={this.props.connectIconType}
        customConnector={this.props.customConnector}
        isLastStep={isLastStep(steps, i)}
        key={`${step.key}-${i}`}
        step={step}
      />
    ));

    const className = bem(this.props.className, toModifiers(this.props.kind));

    return <ol className={className}>{icons}</ol>;
  }
}

// `memoize` since this is often used to build a `steps`
// array for `ZapStepIcons`, and that array needs to be
// `===` for component purity.
export const stepsFromZap = memoize((zap: Zap = emptyObject) => {
  return sortNodes(zap.nodes || []).map(node => ({
    key: node.selected_api || 'unknown',
    isPremium: node.is_premium || node.isPremium,
    isHidden: false,
  }));
});

ZapStepIcons.stepsFromZap = stepsFromZap;

export default ZapStepIcons;
